import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import EventTile from "../components/EventTile/EventTile";
import CardPreview from "../components/CardPreview/CardPreview";
import SuggestSourceUrlModal from "../components/SuggestSourceUrlModal/SuggestSourceUrlModal";
import DiscoveryMapView from "../components/DiscoveryMap/DiscoveryMapView";
import { CATEGORY_LABELS } from "../config/columns";
import { useWeeklyPlan } from "../hooks/useWeeklyPlan";
import { fetchUserCards } from "../api/userCards";
import { fetchLocationMap } from "../api/locations";
import { useAuth } from "../contexts/AuthContext";
import { buildActivityColumns, buildListDescriptorsFromRows } from "../lib/activityColumns";
import { searchableItemText } from "../lib/cardDisplay";
import {
  ACTIVITY_SHARE_PARAM,
  ACTIVITY_SHARE_VALUE,
  getActivitySharePath,
} from "../lib/activityShareDeepLink";
import { buildParentInvitePrefillMessage, buildWhatsAppSendUrl } from "../lib/whatsAppShare";

const DISCOVERY_CATEGORY_ORDER = ["C", "A", "B", "D"];
const PLACEHOLDER_CATEGORIES = new Set(["A", "B", "D"]);
const CATEGORY_WIP_TOOLTIP = "Diese Kategorie ist in Arbeit.";
const LOCATION_PARAM = "loc";
const LOCATION_ALL_VALUE = "all";
const VIEW_PARAM = "view";
const MAP_MODE_PARAM = "mapMode";
const VIEW_GRID = "grid";
const VIEW_MAP = "map";
const MAP_MODE_FILTERED = "filtered";
const MAP_MODE_ALL = "all";
const AGE_RANGE_OPTIONS = [
  { id: "all", label: "Alle", title: "Alle Altersstufen", minMonths: null, maxMonths: null },
  { id: "0-12m", label: "0-12 M", title: "0-12 Monate", minMonths: 0, maxMonths: 12 },
  { id: "1-2y", label: "1-2 J", title: "1-2 Jahre", minMonths: 12, maxMonths: 24 },
  { id: "2-4y", label: "2-4 J", title: "2-4 Jahre", minMonths: 24, maxMonths: 48 },
  { id: "4-6y", label: "4-6 J", title: "4-6 Jahre", minMonths: 48, maxMonths: 72 },
  { id: "6-9y", label: "6-9 J", title: "6-9 Jahre", minMonths: 72, maxMonths: 108 },
  { id: "9-12y", label: "9-12 J", title: "9-12 Jahre", minMonths: 108, maxMonths: 144 },
  { id: "12-16y", label: "12-16 J", title: "12-16 Jahre", minMonths: 144, maxMonths: 192 },
  { id: "16y+", label: "16+ J", title: "16+ Jahre", minMonths: 192, maxMonths: null },
];

function parseAgeHintRangeInMonths(ageHint) {
  if (!ageHint || typeof ageHint !== "string") return null;
  const raw = ageHint.toLowerCase().trim();
  if (!raw) return null;

  const unitMatches = Array.from(
    raw.matchAll(/(\d{1,2})(?:\s*-\s*(\d{1,2}))?\s*(monate?|monat|jahre?|jahr|j\.)/g),
  );
  if (unitMatches.length > 0) {
    let minMonths = Number.POSITIVE_INFINITY;
    let maxMonths = Number.NEGATIVE_INFINITY;
    unitMatches.forEach((m) => {
      const first = Number(m[1]);
      const second = m[2] ? Number(m[2]) : first;
      const unit = m[3];
      const factor = /^mon/.test(unit) ? 1 : 12;
      minMonths = Math.min(minMonths, first * factor);
      maxMonths = Math.max(maxMonths, second * factor);
    });
    if (Number.isFinite(minMonths) && Number.isFinite(maxMonths)) {
      return { minMonths, maxMonths };
    }
  }

  const minYearsMatch = raw.match(/(?:ab|min(?:imal)?|mind\.)\s*(\d{1,2})(?:\s*(?:jahre?|jahr|j\.))?/);
  if (minYearsMatch) {
    const minMonths = Number(minYearsMatch[1]) * 12;
    return { minMonths, maxMonths: null };
  }

  const maxYearsMatch = raw.match(/(?:bis|max(?:imal)?|höchstens)\s*(\d{1,2})(?:\s*(?:jahre?|jahr|j\.))?/);
  if (maxYearsMatch) {
    const maxMonths = Number(maxYearsMatch[1]) * 12;
    return { minMonths: 0, maxMonths };
  }

  const plusYearsMatch = raw.match(/(\d{1,2})\s*\+\s*(?:jahre?|jahr|j\.)?/);
  if (plusYearsMatch) {
    const minMonths = Number(plusYearsMatch[1]) * 12;
    return { minMonths, maxMonths: null };
  }

  return null;
}

function rangesOverlap(aMin, aMax, bMin, bMax) {
  const safeAMax = aMax == null ? Number.POSITIVE_INFINITY : aMax;
  const safeBMax = bMax == null ? Number.POSITIVE_INFINITY : bMax;
  return aMin <= safeBMax && bMin <= safeAMax;
}

function getLocationOptionFromItem(item) {
  const locationId =
    typeof item?.location_id === "string" ? item.location_id.trim() : "";
  const locationLabel =
    typeof item?.location === "string" ? item.location.trim() : "";

  if (locationId && locationLabel) {
    return {
      key: `id:${locationId}`,
      label: locationLabel,
    };
  }

  if (locationLabel) {
    return {
      key: `text:${locationLabel.toLowerCase()}`,
      label: locationLabel,
    };
  }

  return null;
}

function normalizeLocationLabel(value) {
  return String(value || "")
    .replace(/\+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export default function EntdeckenPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [listColumns, setListColumns] = useState([]);
  const dataCategoryOrder = Array.from(
    new Set(listColumns.map((c) => c.category).filter(Boolean)),
  );
  const categoryOrder = [
    ...DISCOVERY_CATEGORY_ORDER,
    ...dataCategoryOrder.filter((cat) => !DISCOVERY_CATEGORY_ORDER.includes(cat)),
  ];
  const defaultCategory = categoryOrder.includes("C") ? "C" : categoryOrder[0] ?? null;
  const canSelectAge = Boolean(user);
  const canSelectColumn = Boolean(user);
  const canSelectLocation = Boolean(user);
  const canChange14DayWindow = Boolean(user);

  const [columns, setColumns] = useState([]);
  const [userCards, setUserCards] = useState([]);
  const [activeColumn, setActiveColumn] = useState(null);
  const [sharedEventIndex, setSharedEventIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState(defaultCategory);
  const [activeCategoryColumn, setActiveCategoryColumn] = useState(null);
  const [activeAgeRangeIds, setActiveAgeRangeIds] = useState(["all"]);
  const [activeLocationKey, setActiveLocationKey] = useState(() => {
    const loc = searchParams.get(LOCATION_PARAM);
    return loc && loc.trim() ? loc : LOCATION_ALL_VALUE;
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestSourceModal, setShowSuggestSourceModal] = useState(false);
  const [showOnlyNext14Days, setShowOnlyNext14Days] = useState(false);
  const [activeView, setActiveView] = useState(() => {
    const view = String(searchParams.get(VIEW_PARAM) || "").trim().toLowerCase();
    return view === VIEW_MAP ? VIEW_MAP : VIEW_GRID;
  });
  const [mapMode, setMapMode] = useState(() => {
    const mode = String(searchParams.get(MAP_MODE_PARAM) || "").trim().toLowerCase();
    return mode === MAP_MODE_ALL ? MAP_MODE_ALL : MAP_MODE_FILTERED;
  });
  const [mapLocations, setMapLocations] = useState([]);
  const [mapMeta, setMapMeta] = useState({
    totalLocations: 0,
    totalActivities: 0,
    missingCoordsCount: 0,
  });
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");

  const { plan, addToSlot, dayDates, getSlotKey } = useWeeklyPlan();
  const planSlots = plan?.slots ?? null;

  const truncate = (text, maxLen) => {
    if (!text) return "";
    const t = String(text).trim();
    if (t.length <= maxLen) return t;
    return `${t.slice(0, Math.max(0, maxLen - 1)).trimEnd()}…`;
  };

  const categoryIcon = (cat) => {
    switch (cat) {
      case "A":
        return "💡";
      case "B":
        return "🍽️";
      case "C":
        return "🎭";
      case "D":
        return "🧘";
      default:
        return cat;
    }
  };

  const SEARCH_PLACEHOLDERS = [
    "Spielplatz bei Regen",
    "GZ Programm",
    "Ruhige Aktivität",
  ];
  const searchPlaceholder =
    SEARCH_PLACEHOLDERS[Math.floor(Date.now() / 86400000) % SEARCH_PLACEHOLDERS.length];

  const categoryForColumnIndex = (colIndex) => listColumns[colIndex]?.category ?? null;

  const matchesSearch = useCallback((item, type) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const searchWords = query.split(/\s+/).filter((word) => word.length > 0);
    if (searchWords.length === 0) return true;
    const searchableText = searchableItemText(item, type);
    return searchWords.every((word) => searchableText.includes(word));
  }, [searchQuery]);

  const matchesAgeRange = useCallback((item) => {
    if (!canSelectAge) return true;
    if (activeAgeRangeIds.includes("all")) return true;
    const selectedRanges = AGE_RANGE_OPTIONS.filter((opt) => activeAgeRangeIds.includes(opt.id));
    if (selectedRanges.length === 0) return true;
    const itemRange = parseAgeHintRangeInMonths(item?.age_hint);
    if (!itemRange) {
      // Unknown ages are intentionally grouped with 16+ requests.
      return activeAgeRangeIds.includes("16y+");
    }
    return selectedRanges.some((selectedRange) =>
      rangesOverlap(
        itemRange.minMonths,
        itemRange.maxMonths,
        selectedRange.minMonths,
        selectedRange.maxMonths,
      ),
    );
  }, [activeAgeRangeIds, canSelectAge]);

  const matchesLocation = useCallback((item) => {
    if (!canSelectLocation) return true;
    if (activeLocationKey === LOCATION_ALL_VALUE) return true;
    const option = getLocationOptionFromItem(item);
    return option?.key === activeLocationKey;
  }, [activeLocationKey, canSelectLocation]);

  const availableLocationOptions = useMemo(() => {
    const hasSearch = searchQuery.trim().length > 0;
    const byLabel = new Map();

    listColumns.forEach((c, colIndex) => {
      if (!hasSearch) {
        if (c.category !== activeCategory) return;
        if (activeCategoryColumn !== null && colIndex !== activeCategoryColumn) return;
      }

      const col = columns[colIndex] ?? [];
      col.forEach((item) => {
        if (!matchesSearch(item, c.type) || !matchesAgeRange(item)) return;
        const option = getLocationOptionFromItem(item);
        if (!option) return;
        const labelKey = normalizeLocationLabel(option.label);
        if (!labelKey) return;
        if (!byLabel.has(labelKey)) {
          byLabel.set(labelKey, {
            key: option.key,
            label: option.label,
            count: 0,
          });
        }
        const bucket = byLabel.get(labelKey);
        bucket.count += 1;
        // Prefer canonical id-based key whenever available for this label.
        if (String(option.key).startsWith("id:")) {
          bucket.key = option.key;
        }
      });
    });

    return [...byLabel.values()]
      .sort((a, b) => {
        if (a.count !== b.count) return b.count - a.count;
        return a.label.localeCompare(b.label, "de-CH");
      });
  }, [
    activeCategory,
    activeCategoryColumn,
    columns,
    listColumns,
    searchQuery,
    matchesAgeRange,
    matchesSearch,
  ]);

  const toggleAgeRange = (rangeId) => {
    setActiveAgeRangeIds((prev) => {
      if (rangeId === "all") return ["all"];
      const withoutAll = prev.filter((id) => id !== "all");
      if (withoutAll.includes(rangeId)) {
        const next = withoutAll.filter((id) => id !== rangeId);
        return next.length > 0 ? next : ["all"];
      }
      return [...withoutAll, rangeId];
    });
  };

  useEffect(() => {
    if (!canSelectAge) setActiveAgeRangeIds(["all"]);
  }, [canSelectAge]);

  useEffect(() => {
    if (!canChange14DayWindow) setShowOnlyNext14Days(false);
  }, [canChange14DayWindow]);

  const handleAgeRangePress = (rangeId) => {
    if (!canSelectAge) {
      navigate(`/login?redirect=${encodeURIComponent("/entdecken")}`);
      return;
    }
    toggleAgeRange(rangeId);
  };

  const handleColumnFilterPress = (colIndexOrNull) => {
    if (!canSelectColumn) {
      navigate(`/login?redirect=${encodeURIComponent("/entdecken")}`);
      return;
    }
    setActiveCategoryColumn(colIndexOrNull);
  };

  const handle14DayWindowPress = () => {
    if (!canChange14DayWindow) {
      navigate(`/login?redirect=${encodeURIComponent("/entdecken")}`);
      return;
    }
    setShowOnlyNext14Days((prev) => !prev);
  };

  useEffect(() => {
    if (searchParams.get(ACTIVITY_SHARE_PARAM) !== ACTIVITY_SHARE_VALUE) return;
    setShowSuggestSourceModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete(ACTIVITY_SHARE_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loc = searchParams.get(LOCATION_PARAM);
    const next = loc && loc.trim() ? loc : LOCATION_ALL_VALUE;
    setActiveLocationKey((prev) => (prev === next ? prev : next));
  }, [searchParams]);

  useEffect(() => {
    if (!canSelectColumn) setActiveCategoryColumn(null);
  }, [canSelectColumn]);

  useEffect(() => {
    if (authLoading || canSelectLocation) return;
    setActiveLocationKey(LOCATION_ALL_VALUE);
    if (!searchParams.has(LOCATION_PARAM)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(LOCATION_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [authLoading, canSelectLocation, searchParams, setSearchParams]);

  useEffect(() => {
    if (activeLocationKey === LOCATION_ALL_VALUE) return;
    const normalizedActiveText =
      activeLocationKey.startsWith("text:")
        ? normalizeLocationLabel(activeLocationKey.slice(5))
        : "";
    const matchingIdOption =
      normalizedActiveText &&
      availableLocationOptions.find(
        (option) =>
          option.key.startsWith("id:") &&
          normalizeLocationLabel(option.label) === normalizedActiveText,
      );
    if (matchingIdOption) {
      setActiveLocationKey(matchingIdOption.key);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(LOCATION_PARAM, matchingIdOption.key);
          return next;
        },
        { replace: true },
      );
      return;
    }
    const stillAvailable = availableLocationOptions.some((option) => option.key === activeLocationKey);
    if (stillAvailable) return;
    setActiveLocationKey(LOCATION_ALL_VALUE);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(LOCATION_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [activeLocationKey, availableLocationOptions, setSearchParams]);

  const searchKey = searchParams.toString();

  useEffect(() => {
    if (columns.length === 0) return;
    const params = new URLSearchParams(searchKey);
    const colId = params.get("column");
    const evt = params.get("event");
    if (!colId && (evt == null || evt === "")) {
      setActiveColumn(null);
      setSharedEventIndex(null);
      return;
    }
    if (colId) {
      const colIndex = listColumns.findIndex((c) => String(c.id) === String(colId));
      if (colIndex !== -1) {
        setActiveColumn(colIndex);
        setActiveCategory(listColumns[colIndex]?.category ?? null);
        setActiveCategoryColumn(colIndex);
      }
    }
    // Require `colId` with `event` so a stale `searchKey` after `goBack` cannot reopen fullscreen.
    if (evt != null && evt !== "" && colId && columns.length > 0) {
      const colIndex = listColumns.findIndex((c) => String(c.id) === String(colId));
      if (colIndex !== -1 && columns[colIndex]) {
        let foundIndex = columns[colIndex].findIndex(
          (e) => e && String(e.id) === String(evt),
        );
        if (foundIndex === -1) {
          const evtNum = Number(evt);
          if (!Number.isNaN(evtNum) && evtNum >= 0 && evtNum < columns[colIndex].length) {
            foundIndex = evtNum;
          }
        }
        if (foundIndex !== -1) setSharedEventIndex(foundIndex);
      }
    }
  }, [columns, listColumns, searchKey]);

  const loadUserCards = useCallback(async () => {
    try {
      const data = await fetchUserCards({
        category: "C",
        includeAll: !showOnlyNext14Days,
      });
      setUserCards(data);
    } catch (err) {
      console.error("Error loading user cards:", err);
      setUserCards([]);
    }
  }, [showOnlyNext14Days]);

  useEffect(() => {
    loadUserCards();
  }, [loadUserCards]);

  useEffect(() => {
    const descriptors = buildListDescriptorsFromRows(userCards);
    setListColumns(descriptors);
    setColumns(buildActivityColumns(descriptors, userCards));
    setActiveCategory((prev) => {
      if (prev != null) return prev;
      return defaultCategory;
    });
  }, [defaultCategory, userCards]);

  useEffect(() => {
    if (activeColumn !== null) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [activeColumn]);

  const openFullscreen = (colIndex, eventIndex = 0) => {
    setSharedEventIndex(eventIndex);
    setActiveColumn(colIndex);
    setActiveCategory(categoryForColumnIndex(colIndex));
    setActiveCategoryColumn(colIndex);
    const columnId = listColumns[colIndex]?.id ?? colIndex;
    const eventId = columns[colIndex]?.[eventIndex]?.id || eventIndex;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("column", String(columnId));
        next.set("event", String(eventId));
        return next;
      },
      { replace: true },
    );
  };

  const goBack = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("column");
        next.delete("event");
        return next;
      },
      { replace: true },
    );
    setActiveColumn(null);
    setSharedEventIndex(null);
  };

  const labels = listColumns.map((c) => c.label);

  const handleSuggestSource = () => {
    if (user) {
      setShowSuggestSourceModal(true);
    } else {
      navigate(`/login?redirect=${encodeURIComponent(getActivitySharePath())}`);
    }
  };

  const handleAskParentsWhatsApp = useCallback(() => {
    const waUrl = buildWhatsAppSendUrl(buildParentInvitePrefillMessage());
    window.open(waUrl, "_blank", "noopener,noreferrer");
  }, []);

  const setLocationFilter = (locationKey) => {
    if (!canSelectLocation) {
      navigate(`/login?redirect=${encodeURIComponent("/entdecken")}`);
      return;
    }
    setActiveLocationKey(locationKey);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (locationKey === LOCATION_ALL_VALUE) next.delete(LOCATION_PARAM);
        else next.set(LOCATION_PARAM, locationKey);
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    const view = String(searchParams.get(VIEW_PARAM) || "").trim().toLowerCase();
    const nextView = view === VIEW_MAP ? VIEW_MAP : VIEW_GRID;
    setActiveView((prev) => (prev === nextView ? prev : nextView));
    const mode = String(searchParams.get(MAP_MODE_PARAM) || "").trim().toLowerCase();
    const nextMode = mode === MAP_MODE_ALL ? MAP_MODE_ALL : MAP_MODE_FILTERED;
    setMapMode((prev) => (prev === nextMode ? prev : nextMode));
  }, [searchParams]);

  const setViewMode = useCallback(
    (view) => {
      const nextView = view === VIEW_MAP ? VIEW_MAP : VIEW_GRID;
      setActiveView(nextView);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextView === VIEW_GRID) next.delete(VIEW_PARAM);
          else next.set(VIEW_PARAM, VIEW_MAP);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setMapDatasetMode = useCallback(
    (nextMode) => {
      const mode = nextMode === MAP_MODE_ALL ? MAP_MODE_ALL : MAP_MODE_FILTERED;
      setMapMode(mode);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (mode === MAP_MODE_FILTERED) next.delete(MAP_MODE_PARAM);
          else next.set(MAP_MODE_PARAM, MAP_MODE_ALL);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const hasSearch = searchQuery.trim().length > 0;
  const visibleCards = useMemo(
    () =>
      listColumns
        .flatMap((c, colIndex) => {
          if (!hasSearch) {
            if (c.category !== activeCategory) return [];
            if (activeCategoryColumn !== null && colIndex !== activeCategoryColumn) return [];
          }
          const col = columns[colIndex] ?? [];
          return col
            .map((item, originalIndex) => ({ item, originalIndex }))
            .filter(
              ({ item }) =>
                matchesSearch(item, c.type) && matchesAgeRange(item) && matchesLocation(item),
            )
            .map(({ item, originalIndex }) => ({
              colIndex,
              item,
              i: originalIndex,
            }));
        }),
    [
      activeCategory,
      activeCategoryColumn,
      columns,
      hasSearch,
      listColumns,
      matchesAgeRange,
      matchesLocation,
      matchesSearch,
    ],
  );

  const loadMapLocations = useCallback(async () => {
    if (activeView !== VIEW_MAP) return;
    setMapLoading(true);
    setMapError("");
    try {
      const payload = await fetchLocationMap({
        mode: mapMode,
        category: activeCategory || "C",
        age: activeAgeRangeIds,
        q: searchQuery,
        loc: activeLocationKey,
        next14: showOnlyNext14Days,
      });
      setMapLocations(Array.isArray(payload?.locations) ? payload.locations : []);
      setMapMeta({
        totalLocations: payload?.meta?.totalLocations ?? 0,
        totalActivities: payload?.meta?.totalActivities ?? 0,
        missingCoordsCount: payload?.meta?.missingCoordsCount ?? 0,
      });
    } catch (err) {
      setMapLocations([]);
      setMapMeta({
        totalLocations: 0,
        totalActivities: 0,
        missingCoordsCount: 0,
      });
      setMapError(err.message || "Karten-Orte konnten nicht geladen werden.");
    } finally {
      setMapLoading(false);
    }
  }, [
    activeAgeRangeIds,
    activeCategory,
    activeLocationKey,
    activeView,
    mapMode,
    searchQuery,
    showOnlyNext14Days,
  ]);

  useEffect(() => {
    loadMapLocations();
  }, [loadMapLocations]);

  const handleMapToggle = (view) => {
    setViewMode(view);
  };

  const handleMapShowInList = (location) => {
    if (canSelectLocation && location?.id) {
      setLocationFilter(`id:${location.id}`);
    }
    setViewMode(VIEW_GRID);
  };

  return (
    <div className="entdecken-page">
      <div className="entdecken-toolbar">
        <button
          type="button"
          className="entdecken-link-teilen-btn"
          onClick={handleSuggestSource}
          aria-label="Aktivität teilen"
        >
          ✨ Aktivität teilen
        </button>
      </div>
      <div className="homepage-search-bar">
        <input
          type="text"
          className="homepage-search-input"
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search cards"
        />
      </div>
      <div className="homepage-filter-bar homepage-filter-bar--side" role="group" aria-label="Zeitfenster-Filter">
        <button
          type="button"
          className={`date-window-toggle ${showOnlyNext14Days ? "active" : ""} ${
            !canChange14DayWindow ? "date-window-toggle--locked" : ""
          }`}
          onClick={handle14DayWindowPress}
          aria-pressed={showOnlyNext14Days}
          aria-disabled={!canChange14DayWindow || undefined}
          title={
            canChange14DayWindow
              ? "Nur Aktivitäten in den nächsten 14 Tagen anzeigen"
              : "Anmelden, um den 14-Tage-Filter zu nutzen"
          }
        >
          <span className="date-window-toggle-label">14 Tage</span>
          <span className="date-window-toggle-switch" aria-hidden="true">
            <span className="date-window-toggle-knob" />
          </span>
        </button>
      </div>
      <div className="homepage-filter-bar" role="group" aria-label="Category filter">
        {categoryOrder.map((cat) => {
          const isUnavailable =
            PLACEHOLDER_CATEGORIES.has(cat) && !dataCategoryOrder.includes(cat);
          const label = CATEGORY_LABELS[cat] ?? cat;
          return (
            <button
              key={cat}
              type="button"
              className={`homepage-filter-btn homepage-filter-btn-with-label ${
                activeCategory === cat ? "active" : ""
              } ${isUnavailable ? "homepage-filter-btn--placeholder" : ""}`}
              onClick={() => {
                if (isUnavailable) return;
                setActiveCategory(cat);
                setActiveCategoryColumn(null);
              }}
              aria-label={`Filter ${label}`}
              aria-disabled={isUnavailable || undefined}
              title={isUnavailable ? CATEGORY_WIP_TOOLTIP : label}
            >
              <span className="homepage-filter-btn-icon">{categoryIcon(cat)}</span>
              {isUnavailable && (
                <span className="homepage-filter-btn-badge" aria-hidden="true">
                  In Arbeit
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div
        className={`homepage-column-filter-bar homepage-column-filter-bar--age ${
          !canSelectAge ? "homepage-column-filter-bar--locked" : ""
        }`}
        role="group"
        aria-label="Altersfilter"
      >
        {AGE_RANGE_OPTIONS.map((range) => (
          <button
            key={range.id}
            type="button"
            className={`homepage-column-filter-btn ${activeAgeRangeIds.includes(range.id) ? "active" : ""}`}
            onClick={() => handleAgeRangePress(range.id)}
            title={canSelectAge ? range.title : "Anmelden, um Altersfilter zu nutzen"}
            aria-pressed={activeAgeRangeIds.includes(range.id)}
            aria-disabled={!canSelectAge || undefined}
          >
            {range.label}
          </button>
        ))}
      </div>
      <div
        className={`homepage-column-filter-bar homepage-column-filter-bar--column ${
          !canSelectColumn ? "homepage-column-filter-bar--locked" : ""
        }`}
        role="group"
        aria-label="Column filter"
      >
        <button
          type="button"
          className={`homepage-column-filter-btn ${activeCategoryColumn === null ? "active" : ""}`}
          onClick={() => handleColumnFilterPress(null)}
          title={canSelectColumn ? "Alle Spalten" : "Anmelden, um Spalten zu filtern"}
          aria-disabled={!canSelectColumn || undefined}
        >
          Alle
        </button>
        {listColumns.map((c, colIndex) => ({ c, colIndex }))
          .filter(({ c }) => c.category === activeCategory)
          .map(({ c, colIndex }) => (
            <button
              key={c.id ?? colIndex}
              type="button"
              className={`homepage-column-filter-btn ${activeCategoryColumn === colIndex ? "active" : ""}`}
              onClick={() => handleColumnFilterPress(colIndex)}
              title={canSelectColumn ? labels[colIndex] : "Anmelden, um Spalten zu filtern"}
              aria-disabled={!canSelectColumn || undefined}
            >
              {truncate(labels[colIndex], 18)}
            </button>
          ))}
      </div>
      {availableLocationOptions.length > 0 && (
        <div
          className={`homepage-column-filter-bar homepage-column-filter-bar--location ${
            !canSelectLocation ? "homepage-column-filter-bar--locked" : ""
          }`}
          role="group"
          aria-label="Ortsfilter"
        >
          <button
            type="button"
            className={`homepage-column-filter-btn ${
              activeLocationKey === LOCATION_ALL_VALUE ? "active" : ""
            }`}
            onClick={() => setLocationFilter(LOCATION_ALL_VALUE)}
            title={canSelectLocation ? "Alle Orte" : "Anmelden, um Orte zu filtern"}
            aria-disabled={!canSelectLocation || undefined}
          >
            Alle Orte
          </button>
          {availableLocationOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`homepage-column-filter-btn ${
                activeLocationKey === option.key ? "active" : ""
              }`}
              onClick={() => setLocationFilter(option.key)}
              title={canSelectLocation ? option.label : "Anmelden, um Orte zu filtern"}
              aria-disabled={!canSelectLocation || undefined}
            >
              {truncate(option.label, 22)}
            </button>
          ))}
        </div>
      )}
      <div className="homepage-column-filter-bar homepage-column-filter-bar--view" role="group" aria-label="Ansicht">
        <button
          type="button"
          className={`homepage-column-filter-btn ${activeView === VIEW_GRID ? "active" : ""}`}
          onClick={() => handleMapToggle(VIEW_GRID)}
        >
          Karten
        </button>
        <button
          type="button"
          className={`homepage-column-filter-btn ${activeView === VIEW_MAP ? "active" : ""}`}
          onClick={() => handleMapToggle(VIEW_MAP)}
        >
          Karte
        </button>
      </div>
      {activeView === VIEW_MAP ? (
        <>
          <div className="homepage-column-filter-bar homepage-column-filter-bar--map-mode" role="group" aria-label="Kartenmodus">
            <button
              type="button"
              className={`homepage-column-filter-btn ${mapMode === MAP_MODE_FILTERED ? "active" : ""}`}
              onClick={() => setMapDatasetMode(MAP_MODE_FILTERED)}
            >
              Gefilterte Orte
            </button>
            <button
              type="button"
              className={`homepage-column-filter-btn ${mapMode === MAP_MODE_ALL ? "active" : ""}`}
              onClick={() => setMapDatasetMode(MAP_MODE_ALL)}
            >
              Alle Orte im Katalog
            </button>
          </div>
          <div className="discovery-map-wrapper">
            <DiscoveryMapView
              locations={mapLocations}
              mode={mapMode}
              isLoading={mapLoading}
              error={mapError}
              missingCoordsCount={mapMeta.missingCoordsCount}
              onRetry={loadMapLocations}
              onBackToList={handleMapShowInList}
            />
          </div>
        </>
      ) : (
        <div className="homepage-cards-grid-wrapper">
          <div
            className="homepage-cards-grid"
            key={`${activeCategory}:${activeCategoryColumn ?? "all-columns"}`}
          >
            {visibleCards.map(({ colIndex, item, i }) => {
              const columnId = listColumns[colIndex]?.id ?? colIndex;
              const eventId = item?.id ?? i;
              return (
                <CardPreview
                  key={`${columnId}:${eventId}:${i}`}
                  item={item}
                  mode="full"
                  columnId={columnId}
                  eventId={eventId}
                  onAddToSlot={addToSlot}
                  planDayDates={dayDates}
                  planSlots={planSlots}
                  getSlotKey={getSlotKey}
                  onClick={() => openFullscreen(colIndex, i)}
                  wrapperClassName="card-preview--square"
                  label={labels[colIndex]}
                  columnType={listColumns[colIndex]?.type ?? "event"}
                />
              );
            })}
          </div>
        </div>
      )}
      {activeColumn !== null && (
        <div
          className="fullscreen-popup-overlay"
          onClick={goBack}
          role="dialog"
          aria-modal="true"
          aria-label="Event details"
        >
          <div
            className="fullscreen-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            {columns[activeColumn] ? (
              <EventTile
                titleLabel={labels[activeColumn]}
                events={columns[activeColumn]}
                columnIndex={activeColumn}
                columnMeta={listColumns[activeColumn]}
                sharedEventIndex={sharedEventIndex}
                isFullscreen={true}
                exitFullscreen={goBack}
                onAddToSlot={addToSlot}
                planDayDates={dayDates}
                planSlots={planSlots}
                getSlotKey={getSlotKey}
              />
            ) : (
              <div className="loading">Loading…</div>
            )}
          </div>
        </div>
      )}
      <p className="feedback-caption">
        Tipp: Füge diese App zum Home-Bildschirm hinzu.
      </p>
      {showSuggestSourceModal && (
        <SuggestSourceUrlModal
          onClose={() => setShowSuggestSourceModal(false)}
          onAskParentsWhatsApp={handleAskParentsWhatsApp}
        />
      )}
    </div>
  );
}
